import { navigate } from "wouter/use-location";

import { Token } from "../stores/slices/authSlice";
import { createSessionStorage } from "./createSessionStorage";
import { HANDOFF_CODE_PATTERN } from "./handoffCode";

export const dealerUrlCode =
  window.dealerAccountInfoPayload?.dealerUrlCode || "global";

const storage = createSessionStorage<StoredSSOToken | null>(
  "tw-sso-token",
  null
);

let pendingHandoffCode: null | string = null;

// NOTE: This is a hack to erase history and avoid the leaking of the token
const eraseHash = (): void => {
  navigate(window.location.pathname + window.location.search, {
    replace: true,
  });
};

// NOTE: Hash is used to pass token from the CMS to the web app and when printing order details
// SEE: code/client/service/HashService.js:decodeUrlSSO
// The CMS-to-site SSO link carries a one-time hand-off code instead of the serialized
// token (TW-1087); the code is kept for exchangePendingHandoffCode, which runs before the
// first render. The serialized form is still produced by Express's /pdf route for the
// headless browser, where the fragment never leaves the process.
export const decodeUrlSSO = (): Token | null => {
  const urlParams = window.location.hash.substring(1).split("/");
  let ssoFound = false;
  let isoFound = false;

  for (let i = 0; i < urlParams.length; i++) {
    if (ssoFound || isoFound) {
      if (HANDOFF_CODE_PATTERN.test(urlParams[i])) {
        pendingHandoffCode = urlParams[i];
        eraseHash();
        return getStoredSSOToken();
      }
      const tokenParams = urlParams[i].split("_");
      if (tokenParams[0].search(/^[A-F0-9]{32}$/) != -1) {
        const token = {
          applicationVersionNumber: tokenParams[5],
          firstName: tokenParams[7],
          lastName: tokenParams[8],
          resetSecurityCheckpoint: Boolean(tokenParams[6]),
          sessionReferenceNumber: tokenParams[4],
          tokenCreatedTimestamp: new Date(
            parseFloat(tokenParams[1]) * 1000
          ).toISOString(),
          tokenExpirationTimestamp: new Date(
            parseFloat(tokenParams[2]) * 1000
          ).toISOString(),
          tokenId: tokenParams[0],
          tokenIsAuthenticated: tokenParams[3] === "1",
          userRoleId: Number(tokenParams[9]),
          userRoleName: tokenParams[10],
        };

        storeSSOToken(token);
        eraseHash();

        return token;
      }
    }
    if (urlParams[i] == "sso") {
      ssoFound = true;
    }
    if (urlParams[i] == "iso") {
      isoFound = true;
    }
  }
  return getStoredSSOToken();
};

const getStoredSSOToken = (): Token | null => {
  const stored = storage.get();
  if (!stored) {
    return null;
  }
  if (
    stored.dealerUrlCode === dealerUrlCode ||
    stored.token?.userRoleId === 1
  ) {
    return stored.token;
  }
  storage.set(null);
  return null;
};

export const clearSSOToken = (): void => {
  storage.set(null);
};

/** Keeps an SSO token across reloads of this tab, like a token taken from the fragment. */
export const storeSSOToken = (token: Token): void => {
  storage.set({ dealerUrlCode, token });
};

/** The hand-off code found in the fragment on load, handed out once. */
export const takePendingHandoffCode = (): null | string => {
  const code = pendingHandoffCode;
  pendingHandoffCode = null;
  return code;
};

export const parseToken = (): Token => {
  const ssoToken = decodeUrlSSO();
  if (ssoToken) {
    return ssoToken;
  }
  return JSON.parse(window.token);
};

type StoredSSOToken = {
  dealerUrlCode: string;
  token: Token;
};

declare global {
  interface Window {
    token: string;
    uniquifyToken: null | string;
  }
}
